import {
  OperationProcessChildRow,
  OperationProcessRow,
  OperationProcessSiteInfo,
} from '../../services/OperationProcessService';
import { normalizeDocLinkHref } from './OperationDocLinksCell';
import { CATEGORIES } from './operationProcessShared';

export interface BuildPdfHtmlParams {
  title: string;
  fixedSiteName?: string;
  hideSiteInfo?: boolean;
  siteInfo: OperationProcessSiteInfo;
  rows: OperationProcessRow[];
  done: number;
  total: number;
  pct: number;
}

/**
 * 운영 프로세스 데이터로부터 인쇄/ PDF 변환용 self-contained HTML 문서를 만든다.
 *
 * SPA 의 MUI 트리(input/scroll 컨테이너 등)를 그대로 window.print() 하던 방식은
 * 입력값이 비어 보이거나 잘리는 문제가 있어, 데이터에서 직접 HTML 을 생성한다.
 * 순수 함수 — DOM/브라우저 의존이 없어 단위 테스트 가능.
 */
export function buildOperationProcessPdfHtml(params: BuildPdfHtmlParams): {
  fileName: string;
  html: string;
} {
  const { title, fixedSiteName, hideSiteInfo, siteInfo, rows, done, total, pct } = params;

  const fileName = fixedSiteName
    ? `운영프로세스_${fixedSiteName}_${new Date().toISOString().slice(0, 10)}`
    : `운영프로세스_${new Date().toISOString().slice(0, 10)}`;

  const escapeHtml = (s: unknown): string => {
    const str = s == null ? '' : String(s);
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
      .replace(/\n/g, '<br/>');
  };
  const categoryLabel = (v: string) =>
    CATEGORIES.find((c) => c.value === v)?.label ?? v;
  const check = (b: boolean) => (b ? '☑' : '☐');

  const headerRow = `
      <tr>
        <th class="check">완료</th>
        <th class="num">No.</th>
        <th>카테고리</th>
        <th>이름</th>
        <th>날짜</th>
        <th>내용</th>
        <th>관련 문서 링크</th>
        <th>작업자</th>
        <th>비고</th>
      </tr>`;
  const childHeader = `
      <tr class="sub-header">
        <th class="check">완료</th>
        <th class="num">순서</th>
        <th>이름</th>
        <th>내용</th>
        <th>날짜</th>
        <th>비고</th>
        <th>작업자</th>
        <th colspan="2"></th>
      </tr>`;
  const buildChildRow = (c: OperationProcessChildRow, idx: number) => `
      <tr class="sub-row${c.checked ? ' done' : ''}">
        <td class="check">${check(c.checked)}</td>
        <td class="num">${idx + 1}</td>
        <td>${escapeHtml(c.name)}</td>
        <td>${escapeHtml(c.content)}</td>
        <td>${escapeHtml(c.date)}</td>
        <td>${escapeHtml(c.remark)}</td>
        <td>${escapeHtml(c.worker)}</td>
        <td colspan="2"></td>
      </tr>`;
  const buildRow = (r: OperationProcessRow, idx: number) => {
    const children = Array.isArray(r.children) ? r.children : [];
    const main = `
        <tr class="main${r.checked ? ' done' : ''}">
          <td class="check">${check(r.checked)}</td>
          <td class="num">${idx + 1}</td>
          <td>${escapeHtml(categoryLabel(r.category))}</td>
          <td>${escapeHtml(r.name)}</td>
          <td>${escapeHtml(r.date)}</td>
          <td>${escapeHtml(r.content)}</td>
          <td>${(Array.isArray(r.docLinks) ? r.docLinks : [])
            .filter((l) => (l.url ?? '').trim())
            .map(
              (l) =>
                `<a href="${escapeHtml(normalizeDocLinkHref(l.url))}">${escapeHtml(
                  (l.label ?? '').trim() || l.url
                )}</a>`
            )
            .join('<br/>')}</td>
          <td>${escapeHtml(r.worker)}</td>
          <td>${escapeHtml(r.remark)}</td>
        </tr>`;
    if (children.length === 0) return main;
    return (
      main +
      childHeader +
      children.map((c, i) => buildChildRow(c, i)).join('')
    );
  };

  const siteInfoBlock = !hideSiteInfo
    ? `
        <table class="site-info">
          <tbody>
            <tr>
              <th>현장명</th>
              <td>${escapeHtml(fixedSiteName ?? siteInfo.siteName)}</td>
              <th>위치</th>
              <td>${escapeHtml(siteInfo.location)}</td>
              <th>담당자</th>
              <td>${escapeHtml(siteInfo.manager)}</td>
              <th>고객사</th>
              <td>${escapeHtml(siteInfo.customer)}</td>
            </tr>
            <tr>
              <th>시작일</th>
              <td>${escapeHtml(siteInfo.startDate)}</td>
              <th>완료 예정</th>
              <td>${escapeHtml(siteInfo.endDate)}</td>
              <th>연락처</th>
              <td>${escapeHtml(siteInfo.contact)}</td>
              <th>현장코드</th>
              <td>${escapeHtml(siteInfo.siteCode)}</td>
            </tr>
            <tr>
              <th>비고</th>
              <td colspan="7">${escapeHtml(siteInfo.remark)}</td>
            </tr>
          </tbody>
        </table>`
    : '';

  const html = `<!doctype html>
<html lang="ko">
<head>
<meta charset="utf-8" />
<title>${escapeHtml(fileName)}</title>
<style>
  @page { size: A4 landscape; margin: 10mm; }
  * { box-sizing: border-box; }
  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Apple SD Gothic Neo', 'Malgun Gothic', sans-serif;
    margin: 0;
    color: #111;
    font-size: 11px;
    line-height: 1.4;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  h1 { font-size: 18px; margin: 0 0 8px; letter-spacing: -0.01em; }
  .meta { font-size: 10px; color: #666; margin: 0 0 12px; }
  .progress {
    display: inline-block; padding: 2px 8px; border-radius: 999px;
    background: #eef2ff; color: #4338ca; font-weight: 600;
    margin-left: 8px; font-size: 10px;
  }
  table { width: 100%; border-collapse: collapse; }
  table.main { border: 1px solid #999; }
  table.site-info { margin-bottom: 10px; border: 1px solid #ccc; }
  table.site-info th, table.site-info td {
    border: 1px solid #ddd; padding: 4px 6px; vertical-align: top;
  }
  table.site-info th {
    background: #f5f5f5; color: #444; font-weight: 600;
    width: 60px; text-align: left;
  }
  thead { display: table-header-group; }
  th, td {
    border: 1px solid #d4d4d4;
    padding: 5px 6px;
    vertical-align: top;
    word-break: break-word;
    overflow-wrap: break-word;
  }
  th { background: #f0f4ff; color: #1f2937; font-weight: 700; font-size: 10.5px; }
  td.check, td.num, th.check, th.num { text-align: center; white-space: nowrap; width: 36px; }
  tr { page-break-inside: avoid; }
  tr.done td { color: #999; text-decoration: line-through; }
  tr.sub-header th {
    background: #fafbff; color: #4b5563; font-size: 9.5px; font-weight: 600;
  }
  tr.sub-row td {
    background: #fafbff; font-size: 10px; padding-left: 14px;
  }
  tr.sub-row td.check, tr.sub-row td.num { padding-left: 6px; }
  .footer { margin-top: 10px; font-size: 9px; color: #888; text-align: right; }
</style>
</head>
<body>
  <h1>
    ${escapeHtml(title)}
    ${fixedSiteName ? `<span style="color:#2563eb;margin-left:8px;">${escapeHtml(fixedSiteName)}</span>` : ''}
    <span class="progress">${done} / ${total} (${pct}%)</span>
  </h1>
  <div class="meta">출력일: ${new Date().toLocaleString('ko-KR')}</div>
  ${siteInfoBlock}
  <table class="main">
    <thead>${headerRow}</thead>
    <tbody>
      ${rows.length === 0
        ? '<tr><td colspan="9" style="text-align:center;padding:24px;color:#888;">등록된 항목이 없습니다.</td></tr>'
        : rows.map((r, i) => buildRow(r, i)).join('')}
    </tbody>
  </table>
  <div class="footer">${escapeHtml(fileName)}</div>
  <script>
    // 폰트/이미지 로드 안정화 후 인쇄.
    window.addEventListener('load', function () {
      setTimeout(function () {
        window.focus();
        window.print();
      }, 150);
    });
    // 인쇄 다이얼로그 닫힌 후 자동 닫기 (대부분 브라우저에서 동작).
    window.addEventListener('afterprint', function () {
      setTimeout(function () { window.close(); }, 100);
    });
  </script>
</body>
</html>`;

  return { fileName, html };
}
